import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { KeyRound, ShieldCheck, Lock, Eye, EyeOff, RotateCcw, CalendarClock, Save } from "lucide-react";
import { toast } from "sonner";

const errMsg = (e) => e?.response?.data?.detail || "Une erreur est survenue";

function PasswordInput({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        data-testid={testid}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={`${testid}-toggle`}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        aria-label={show ? "Masquer" : "Afficher"}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function ChangeAdminCode() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };
  const save = async () => {
    if (next.length < 6) { toast.error("Le nouveau code doit contenir au moins 6 caractères"); return; }
    if (next !== confirm) { toast.error("La confirmation ne correspond pas"); return; }
    setBusy(true);
    try {
      await api.post("/admin/change-staff-code", { current_code: current, new_code: next });
      toast.success("Code Admin modifié. Il sera demandé à la prochaine connexion.");
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="open-change-admin-code" className="gap-2">
          <KeyRound className="h-4 w-4" /> Changer le code Admin
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Changer le code d'accès Admin</DialogTitle>
          <DialogDescription>Saisissez le code actuel puis le nouveau code. Il protège les espaces Admin, Usine et Transporteur.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Code actuel</Label>
            <PasswordInput testid="admin-current-code" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div>
            <Label>Nouveau code</Label>
            <PasswordInput testid="admin-new-code" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Au moins 6 caractères" />
          </div>
          <div>
            <Label>Confirmer le nouveau code</Label>
            <PasswordInput testid="admin-confirm-code" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid="save-admin-code">{busy ? "..." : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangePin({ enseigne, onDone }) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (pin.length < 6) { toast.error("Le PIN doit contenir au moins 6 caractères"); return; }
    if (pin !== confirm) { toast.error("La confirmation ne correspond pas"); return; }
    setBusy(true);
    try {
      await api.put(`/enseignes/${encodeURIComponent(enseigne)}/pin`, { pin });
      toast.success(`PIN de « ${enseigne} » mis à jour`);
      setOpen(false);
      setPin(""); setConfirm("");
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setPin(""); setConfirm(""); } }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`open-change-pin-${enseigne}`} className="gap-1.5">
          <Lock className="h-3.5 w-3.5" /> Modifier
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>PIN — {enseigne}</DialogTitle>
          <DialogDescription>Ce PIN permet à l'enseigne {enseigne} de se connecter à son espace de commande.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nouveau PIN</Label>
            <PasswordInput testid="pin-new" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Au moins 6 caractères" />
          </div>
          <div>
            <Label>Confirmer le PIN</Label>
            <PasswordInput testid="pin-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid="save-pin">{busy ? "..." : "Enregistrer le PIN"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPin({ enseigne, onDone }) {
  const [busy, setBusy] = useState(false);
  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/enseignes/${encodeURIComponent(enseigne)}/reset-pin`);
      const emailNote = data.email_count > 0
        ? `Envoyé par email à ${data.email_count} dépôt(s).`
        : "Aucun email valide en fiche — communiquez-le manuellement.";
      toast.success(`Nouveau PIN pour « ${enseigne} » : ${data.pin}`, {
        description: `${emailNote} Notez-le maintenant : il ne sera plus affiché.`,
        duration: 12000,
      });
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`open-reset-pin-${enseigne}`} className="gap-1.5 text-secondary">
          <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Réinitialiser le PIN de « {enseigne} » ?</AlertDialogTitle>
          <AlertDialogDescription>Un nouveau code à 6 chiffres sera généré. L'ancien PIN ne fonctionnera plus. À utiliser en cas d'oubli.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-reset-pin">Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={doReset} disabled={busy} data-testid={`confirm-reset-pin-${enseigne}`}>Réinitialiser</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DueDaysCell({ enseigne, dueDays, onDone }) {
  const [value, setValue] = useState(String(dueDays));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue(String(dueDays)); }, [dueDays]);
  const dirty = value !== "" && Number(value) !== dueDays;

  const save = async () => {
    const n = parseInt(value, 10);
    if (!Number.isInteger(n) || n < 1 || n > 90) { toast.error("Échéance invalide (1 à 90 jours)"); return; }
    setBusy(true);
    try {
      await api.put(`/enseignes/${encodeURIComponent(enseigne)}/due-days`, { due_days: n });
      toast.success(`Échéance de « ${enseigne} » : ${n} jours`);
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="number"
        min={1}
        max={90}
        className="h-8 w-16 tabular"
        data-testid={`due-days-input-${enseigne}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <span className="text-xs text-muted-foreground">jours</span>
      {dirty && (
        <Button size="icon" className="h-8 w-8" onClick={save} disabled={busy} data-testid={`save-due-days-${enseigne}`}>
          <Save className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function PinCell({ hasPin }) {
  if (!hasPin) {
    return <Badge variant="outline" className="border-secondary/40 bg-secondary/10 text-secondary">Aucun — réinitialisez</Badge>;
  }
  return (
    <span className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="pin-status">
      <Lock className="h-3.5 w-3.5" /> Défini (masqué)
    </span>
  );
}

export const SecurityManager = () => {
  const [enseignes, setEnseignes] = useState([]);
  const load = () => api.get("/enseignes").then((r) => setEnseignes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <Card className="overflow-hidden" data-testid="security-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-display font-bold">Sécurité &amp; accès</h3>
            <p className="text-xs text-muted-foreground">Gérez votre code Admin et le PIN de connexion de chaque distributeur.</p>
          </div>
        </div>
        <ChangeAdminCode />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Distributeur</TableHead>
            <TableHead>Code PIN</TableHead>
            <TableHead>Échéance factures</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {enseignes.map((e) => (
            <TableRow key={e.name} data-testid={`enseigne-security-row-${e.name}`}>
              <TableCell className="font-medium">{e.name}</TableCell>
              <TableCell><PinCell hasPin={e.has_pin} /></TableCell>
              <TableCell><DueDaysCell enseigne={e.name} dueDays={e.due_days} onDone={load} /></TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1.5">
                  <ChangePin enseigne={e.name} onDone={load} />
                  <ResetPin enseigne={e.name} onDone={load} />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {enseignes.length === 0 && (
            <TableRow><TableCell colSpan={4} className="py-6 text-center text-muted-foreground">Aucun distributeur.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
};
